import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import parser from 'parse-address'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import moment from 'moment-timezone';

import { Input, PhoneInputField, SelectField, TextArea } from '../../containers/Form';
import { notificationActions } from '../../components/Notification/redux/actions';
import { CREATE_ORDER_RESULT, GET_ORDER_ERROR, EDIT_ORDER_RESULT, shippingActions, ACCEPT_ORDER_RESULT } from './actions';
import { actions as orgActions } from '../Organizations/redux/actions';
import { actions as patientActions } from '../SuperUser/Patients/redux/actions';
import Strings from '../../Strings';
import { EndedEnrollmentShippingWarning } from '../../components/EnrollmentShippingWarning';
import { PageHeader } from '../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import { OrderStatusEnum, OrderTypeEnum } from './ShippingOrders';
import { getTooltipArrayFromValidationErrors } from '../../modals/_ModalsMetadata/ModalsMetadata';
import { objectToTooltip } from '../../utils';
import { ShippingMethods, defaultShippingMethod } from '../../modals/EditShipmentModal';
import { openModalAction } from '../../actions/modal';
import { ORGANIZATION_LIST_RESULT } from '../Organizations/redux/constants';
import { DATE_MONTH_DAY_YEAR, DATE_MONTH_DAY_YEAR_WITH_DASH } from '../../constants';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';
import { isShipper, isSuperUser } from '../../utils/userRoles';
import { RejectOrderModal } from './OrdersForReview';
import { loadOrderableDevices } from './orderDeviceLoading';
import { smartmeterLineItemParts } from './smartmeterLineItems';
import {
  VARIANT_NOT_YET_SELECTED,
  getQuantitiesFromOrderItems,
  hasPositiveQuantity,
  mergeCatalogWithStoredItems,
  buildUnknownAvailabilityDevices,
  buildCurrentOrderItems,
  clampQuantity,
  getUnavailableItemsInOrder,
  flattenDevicesToRows,
  sumQuantity,
} from './orderItemsLogic';
import AddressFields from '../../components/AddressFields/AddressFields';

const MAX_QUANTITY = 100;
const MAX_SMARTMETER_QUANTITY = 1;

function renderDeviceModelCell({ row }) {
  const { device, variant, unavailable } = row.original;
  const variantLabel = variant?.label === VARIANT_NOT_YET_SELECTED
    ? (device.smartmeter_variant_required ? Strings.shipping.selectVariant : undefined)
    : variant?.label;
  return (
    <>
      {device.device_model}
      {variantLabel ? ` — ${variantLabel}` : ''}
      {unavailable && <span className="quantity-chip">{Strings.shipping.deviceUnavailable}</span>}
    </>
  );
}

const normalizeOrderItemsForComparison = orderItems => {
  return (orderItems || [])
    .map(item => ({
      device_uuid: item.device_uuid,
      quantity: Number(item.quantity) || 0,
      variant_label: item.variant_label || undefined,
    }))
    .filter(item => item.device_uuid && item.quantity > 0)
    .sort((left, right) => left.device_uuid.localeCompare(right.device_uuid));
};

const buildInitialOrderState = (order, isEditMode) => ({
  targetOrgUuid: order?.target_org_uuid,
  billedOrgUuid: order?.billed_to_org_uuid,
  recipientName: order?.contact_details?.recipient_name,
  email: order?.contact_details?.email,
  phone: order?.contact_details?.phone,
  address: {
    street: order?.shipping_address?.street,
    street2: order?.shipping_address?.street2,
    city: order?.shipping_address?.city,
    state: order?.shipping_address?.state,
    zip: order?.shipping_address?.zip,
    country: order?.shipping_address?.country,
  },
  orderType: order?.order_type || OrderTypeEnum.standard,
  orderCategory: order?.order_category,
  quantities: getQuantitiesFromOrderItems(order?.order_items),
  orderItems: order?.order_items || [],
  patientUuid: order?.patient_uuid,
  enrollmentUuids: order?.enrollment_uuids || [],
  endedEnrollments: order?.ended_enrollments || [],
  isPatientBilled: order?.is_patient_billed || false,
  providerOrderNumber: order?.po_number,
  shippingMethod: order?.shipping_method || defaultShippingMethod,
  orderStatus: order?.status,
  hasShipments: order?.shipments?.length > 0 || isEditMode,
  notes: order?.notes,
  shipments: order?.shipments || [],
  statusHistory: order?.status_history || [],
  sourceKitUuid: order?.source_kit_uuid,
  isReturnFlow: order?.flow === 'return',
});

const buildOrderObjectFromState = (params) => ({
  target_org_uuid: params.targetOrgUuid,
  billed_to_org_uuid: params.billedOrgUuid,
  patient_uuid: params.patientUuid,
  enrollment_uuids: params.enrollmentUuids,
  is_patient_billed: params.isPatientBilled,
  po_number: params.providerOrderNumber,
  shipping_method: params.shippingMethod,
  contact_details: {
    recipient_name: params.recipientName,
    email: params.email || undefined,
    phone: params.phone || undefined,
  },
  shipping_address: {
    street: params.address.street,
    street2: params.address.street2 || undefined,
    city: params.address.city,
    state: params.address.state || undefined,
    zip: params.address.zip,
    country: params.address.country || undefined,
  },
  order_type: params.orderType,
  order_items: params.orderItems,
  notes: params.notes,
});

const normalizeOrderForComparison = order => {
  const base = buildOrderObjectFromState({
    targetOrgUuid: order?.target_org_uuid,
    billedOrgUuid: order?.billed_to_org_uuid,
    patientUuid: order?.patient_uuid,
    enrollmentUuids: order?.enrollment_uuids,
    isPatientBilled: order?.is_patient_billed,
    providerOrderNumber: order?.po_number,
    shippingMethod: order?.shipping_method,
    recipientName: order?.contact_details?.recipient_name,
    email: order?.contact_details?.email,
    phone: order?.contact_details?.phone,
    address: {
      street: order?.shipping_address?.street,
      street2: order?.shipping_address?.street2,
      city: order?.shipping_address?.city,
      state: order?.shipping_address?.state,
      zip: order?.shipping_address?.zip,
      country: order?.shipping_address?.country,
    },
    orderType: order?.order_type,
    orderItems: order?.order_items,
    notes: order?.notes,
  });

  return {
    target_org_uuid: base.target_org_uuid,
    billed_to_org_uuid: base.billed_to_org_uuid,
    patient_uuid: base.patient_uuid,
    enrollment_uuids: [...(base.enrollment_uuids || [])].sort(),
    is_patient_billed: Boolean(base.is_patient_billed),
    po_number: base.po_number,
    shipping_method: base.shipping_method || defaultShippingMethod,
    contact_details: {
      recipient_name: base.contact_details.recipient_name?.trim(),
      email: base.contact_details.email?.trim(),
      phone: base.contact_details.phone?.trim(),
    },
    shipping_address: {
      street: base.shipping_address.street?.trim(),
      street2: base.shipping_address.street2?.trim(),
      city: base.shipping_address.city?.trim(),
      state: base.shipping_address.state?.trim(),
      zip: base.shipping_address.zip?.trim(),
      country: base.shipping_address.country?.trim(),
    },
    order_type: base.order_type || OrderTypeEnum.standard,
    order_items: normalizeOrderItemsForComparison(base.order_items),
    notes: base.notes,
  };
};

function OrderDetails(props) {
  const orderId = React.useMemo(() => props.match.params.id, [props.match.params.id]);
  const { filters, location, showShipmentDetails } = props;
  const { createOrder, updateOrder: updateOrderRequest, rejectOrder, acceptOrder } = props;
  const { getOrder, showNotification, getCatalog, getOrgConfig, getOrgs, getNotes } = props;
  const isEditMode = orderId && orderId !== 'new';
  const data = location?.state;
  const suggestedReturnTargets = data?.order?.suggested_return_targets || [];

  // Form state
  const [order, setOrder] = React.useState(() => buildInitialOrderState(data?.order, isEditMode));

  const {
    targetOrgUuid,
    billedOrgUuid,
    recipientName,
    email,
    phone,
    address,
    orderType,
    orderCategory,
    quantities,
    patientUuid,
    isPatientBilled,
    providerOrderNumber,
    shippingMethod,
    orderStatus,
    hasShipments,
    notes,
    shipments,
    statusHistory,
    sourceKitUuid,
    isReturnFlow,
    orderItems: storedOrderItems,
  } = order;

  const updateOrder = React.useCallback((patchOrUpdater) => {
    setOrder(prev => {
      if (typeof patchOrUpdater === 'function') {
        return patchOrUpdater(prev);
      }
      return {
        ...prev,
        ...patchOrUpdater,
      };
    });
  }, []);

  const [organizations, setOrganizations] = React.useState([]);
  const [orderableDevices, setOrderableDevices] = React.useState([]);
  const [proposedRecipient, setProposedRecipient] = React.useState(null);
  const [proposedAddress, setProposedAddress] = React.useState(null);
  const [orgs, setOrgs] = React.useState([]);
  const [patientNotes, setPatientNotes] = React.useState([]);
  const [smartmeterOrderInfo, setSmartmeterOrderInfo] = React.useState(null);
  const [orderLoaded, setOrderLoaded] = React.useState(!isEditMode);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [activeDeviceType, setActiveDeviceType] = React.useState('');
  const [deviceView, setDeviceView] = React.useState('grid'); // 'grid' or 'list'
  const [showRejectModal, setShowRejectModal] = React.useState(false);
  const [originalOrderSnapshot, setOriginalOrderSnapshot] = React.useState(null);
  // 'loading' | 'ready' | 'error' — outbound orders must not be submittable
  // until the picker reflects the current target org's catalog.
  const [catalogState, setCatalogState] = React.useState(data?.order?.flow === 'return' ? 'ready' : 'loading');
  const [catalogRetryCounter, setCatalogRetryCounter] = React.useState(0);

  const storedQuantities = React.useMemo(() => getQuantitiesFromOrderItems(storedOrderItems), [storedOrderItems]);

  const canEdit = React.useMemo(() =>
    (hasPermission(PERMISSIONS.SHIPPING_ORDERS_EDIT) || isShipper() || isSuperUser())
    && (orderId === 'new' || orderStatus === OrderStatusEnum.draft || orderStatus === OrderStatusEnum.open || orderStatus === OrderStatusEnum.rejected_by_doctor),
  [orderId, orderStatus]);
  const canEditOrdersInReview =
  React.useMemo(() => hasPermission(PERMISSIONS.SHIPPING_PENDING_REVIEW_ORDERS_EDIT) && orderStatus === OrderStatusEnum.pending_review, [orderStatus]);

  // A failed or still-running catalog load must block fulfillment actions on
  // outbound orders — submitting then could carry stale or dropped devices.
  const catalogNotReady = !isReturnFlow && !hasShipments && catalogState !== 'ready';

  const userSelectedOrg = React.useRef(false);
  const hasOrderItemsValidationError = React.useMemo(
    () => validationErrors.some(v => v.property === 'orderItems'),
    [validationErrors],
  );

  React.useEffect(() => {
    getOrgs().then(resp => {
      if (resp?.type === ORGANIZATION_LIST_RESULT) {
        setOrgs(
          resp.response?.data?.map(o => {
            return { ...o, value: o.masked_id, label: o.name };
          }),
        );
      }
    });
  }, [getOrgs]);

  const getOrderDetails = React.useCallback(() => {
    getOrder(orderId).then(resp => {
      if (resp?.type === GET_ORDER_ERROR) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
      } else {
        const nextOrderState = buildInitialOrderState(resp?.response, false);
        setOriginalOrderSnapshot(normalizeOrderForComparison(resp?.response));
        updateOrder(nextOrderState);
        setActiveDeviceType(resp?.response?.order_items?.length > 0 ? resp?.response?.order_items[0]?.device_type : '');
        setSmartmeterOrderInfo(resp?.response?.smartmeter_fulfillment || null);
      }
      setOrderLoaded(true);
    });
  }, [getOrder, orderId, showNotification, updateOrder]);

  React.useEffect(() => {
    if (orderId && orderId !== 'new') {
      // In edit mode, if order data is not passed in props, we should fetch it
      getOrderDetails();
    } else if (isEditMode && data?.order) {
      // For new orders or when data is passed via route state, normalize it
      setOriginalOrderSnapshot(normalizeOrderForComparison(data.order));
    }
  }, [orderId, getOrderDetails, isEditMode, data?.order]);

  const parseAddress = addressToParse => {
    if (addressToParse) {
      const parsedAddress = parser.parseLocation(addressToParse);
      updateOrder({
        address: {
          street: `${parsedAddress?.number ? `${parsedAddress?.number} ` : ''}${parsedAddress?.street}${parsedAddress?.type ? ` ${parsedAddress?.type}` : ''}`,
          street2: parsedAddress?.sec_unit_type ? `${parsedAddress?.sec_unit_type}${parsedAddress?.sec_unit_num ? ` ${parsedAddress?.sec_unit_num}` : ''}` : '',
          city: parsedAddress?.city,
          state: parsedAddress?.state,
          zip: `${parsedAddress?.plus4 ? `${parsedAddress?.plus4}-` : ''}${parsedAddress?.zip}`,
          country: 'USA',
        },
      });
    }
    return null;
  };

  const onAddressChange = React.useCallback((name, value) => {
    updateOrder(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  }, [updateOrder]);

  React.useEffect(() => {
    if (!orderLoaded) {
      return;
    }
    getOrgs({ limit: 1000, offset: 0 }).then(resp => {
      if (resp?.type === ORGANIZATION_LIST_RESULT) {
        setOrganizations(resp.response?.data || []);
      } else {
        setOrganizations([]);
      }
    });
  }, [getOrgs, getCatalog, orderLoaded]);

  React.useEffect(() => {
    if (isReturnFlow) {
      setCatalogState('ready');
      return undefined;
    }
    if (!targetOrgUuid) {
      // No org to load a catalog for (new bulk order before org selection, or
      // a legacy order stored without a target org). Show stored items with
      // unknown availability so they stay in the DTO and the order remains
      // placeable; quantities are not editable without a catalog.
      updateOrder(prevOrder => {
        setOrderableDevices(buildUnknownAvailabilityDevices(prevOrder.orderItems));
        return prevOrder;
      });
      setCatalogState('ready');
      return undefined;
    }
    let cancelled = false;
    setCatalogState('loading');
    loadOrderableDevices({ isReturnFlow, targetOrgUuid, getCatalog }).then(result => {
      if (cancelled || result.skipped) {
        return;
      }
      if (result.error) {
        showNotification(result.error?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
        setCatalogState('error');
        return;
      }
      // Merge inside the state updater so a concurrently arriving getOrder
      // result cannot be clobbered by a stale closure.
      updateOrder(prevOrder => {
        const merged = mergeCatalogWithStoredItems({
          catalogDevices: result.devices,
          storedOrderItems: prevOrder.orderItems,
          prevQuantities: prevOrder.quantities,
        });
        setOrderableDevices(merged.orderableDevices);
        setActiveDeviceType(prevType => {
          if (prevType && merged.orderableDevices.some(d => d.device_type === prevType)) {
            return prevType;
          }
          return merged.orderableDevices[0]?.device_type || '';
        });
        return {
          ...prevOrder,
          quantities: merged.quantities,
        };
      });
      setCatalogState('ready');
    });
    return () => { cancelled = true; };
  }, [targetOrgUuid, isReturnFlow, orderLoaded, catalogRetryCounter, getCatalog, updateOrder, showNotification]);

  React.useEffect(() => {
    if (targetOrgUuid && orderCategory !== "direct") {
      getOrgConfig(targetOrgUuid).then(resp => {
        if (userSelectedOrg.current) {
          parseAddress(resp?.response?.config?.contact_details?.address);
          updateOrder({
            phone: resp?.response?.config?.contact_details?.primary_contact?.phone,
            email: resp?.response?.config?.contact_details?.primary_contact?.email,
            recipientName: resp?.response?.config?.contact_details?.primary_contact?.name,
          });
        }
        setProposedRecipient(resp?.response?.config?.contact_details?.primary_contact || null);
        setProposedAddress(resp?.response?.config?.contact_details?.address || null);
      });
    }

    // Auto-fill billing only for bulk outbound orders. Direct orders and
    // returns must keep billed_to_org_uuid unset: invoicing resolves a NULL
    // billed org to the patient org's oldest ancestor, and returns are never
    // billed at all.
    if (targetOrgUuid && !billedOrgUuid && !isReturnFlow && !patientUuid) {
      updateOrder({ billedOrgUuid: targetOrgUuid });
      // Update the original snapshot to reflect auto-filled billed_to_org_uuid
      if (originalOrderSnapshot) {
        setOriginalOrderSnapshot({
          ...originalOrderSnapshot,
          billed_to_org_uuid: targetOrgUuid,
        });
      }
    }
  }, [targetOrgUuid, getOrgConfig, orderCategory, isEditMode, originalOrderSnapshot, billedOrgUuid, isReturnFlow, patientUuid, updateOrder]);

  // Kit ID to display on the Return Order details page. Direct returns carry
  // the kit on the order itself (source_kit_uuid); bulk returns carry kits on
  // their shipment legs, so fall back to the shipment-attached kits when the
  // order has no source kit.
  const returnKitId = React.useMemo(() => {
    if (sourceKitUuid) return sourceKitUuid;
    const fromShipments = Array.from(new Set(
      (shipments || []).flatMap(s => s.kit_uuids || [])
    ));
    return fromShipments.join(', ');
  }, [sourceKitUuid, shipments]);

  // Validation effect
  React.useEffect(() => {
    const errors = [];
    if (!targetOrgUuid?.trim() && (!patientUuid || isReturnFlow)) {
      errors.push({ property: 'targetOrgUuid', propertyName: Strings.shipping.targetOrganization, missing: true, errors: [] });
    }
    if (!recipientName?.trim()) {
      errors.push({ property: 'recipientName', propertyName: Strings.shipping.recipientName, missing: true, errors: [] });
    }
    if (!address?.street?.trim()) {
      errors.push({ property: 'street', propertyName: Strings.streetAddress, missing: true, errors: [] });
    }
    if (!address?.city?.trim()) {
      errors.push({ property: 'city', propertyName: Strings.city, missing: true, errors: [] });
    }
    if (!address?.zip?.trim()) {
      errors.push({ property: 'zip', propertyName: Strings.zipCode, missing: true, errors: [] });
    }
    if (!hasPositiveQuantity(quantities) && !returnKitId) {
      errors.push({ property: 'orderItems', propertyName: Strings.devices, missing: true, errors: [] });
    }
    if (quantities) {
      Object.entries(quantities).forEach(([deviceUuid, qty]) => {
      const device = orderableDevices.find(d => d.device_uuid === deviceUuid);
      if (device?.smartmeter_variant_required && device?.smartmeter_variants?.length > 0) {
        if (qty[VARIANT_NOT_YET_SELECTED] > 0
          && !device.unavailableVariants?.includes(VARIANT_NOT_YET_SELECTED)) {
          errors.push({
            property: 'variant',
            propertyName: Strings.formatString(Strings.shipping.variantFor, `${device.device_manufacturer} ${device.device_model}`),
            device_uuid: deviceUuid,
            deviceType: device.device_type,
            missing: true,
            errors: []
          });
        }
      }});

      Object.entries(quantities).forEach(([deviceUuid, qty]) => {
        const device = orderableDevices.find(d => d.device_uuid === deviceUuid);
        const isSmartmeter = device?.device_manufacturer === 'smartmeter';
        const deviceName = `${device?.device_manufacturer || ''} ${device?.device_model || deviceUuid}`.trim();
        const pushMaxQuantityError = (variantLabel) => {
          const variantSuffix = variantLabel ? ` (${variantLabel})` : '';
          errors.push({
            property: 'quantityMax',
            propertyName: `${deviceName}${variantSuffix}`,
            device_uuid: deviceUuid,
            deviceType: device?.device_type,
            variant_label: variantLabel,
            missing: false,
            errors: [`Maximum allowed quantity is ${isSmartmeter ? MAX_SMARTMETER_QUANTITY : MAX_QUANTITY}.`],
          });
        };

        if (typeof qty === 'number') {
          if (isSmartmeter ? qty > MAX_SMARTMETER_QUANTITY : qty > MAX_QUANTITY) {
            pushMaxQuantityError();
          }
          if (isSmartmeter && qty > 0 && orderCategory !== 'direct') {
            errors.push({
              property: 'quantity',
              propertyName: `${deviceName}`,
              device_uuid: deviceUuid,
              deviceType: device?.device_type,
              missing: false,
              errors: [`Smartmeters can only be ordered in direct orders to patients.`],
            });
          }
          return;
        }

        if (qty && typeof qty === 'object') {
          Object.entries(qty).forEach(([variantLabel, variantQty]) => {
            if ((isSmartmeter ? variantQty > MAX_SMARTMETER_QUANTITY : variantQty > MAX_QUANTITY)) {
              pushMaxQuantityError(variantLabel === VARIANT_NOT_YET_SELECTED ? Strings.shipping.selectVariant : variantLabel);
            }
            if (isSmartmeter && variantQty > 0 && orderCategory !== 'direct') {
              errors.push({
                property: 'quantity',
                propertyName: `${deviceName} (${variantLabel})`,
                device_uuid: deviceUuid,
                deviceType: device?.device_type,
                variant_label: variantLabel,
                missing: false,
                errors: [`Smartmeters can only be ordered in direct orders to patients.`],
              });
            }
          });
        }
      });
    }
    if (!hasShipments && !isReturnFlow) {
      getUnavailableItemsInOrder(orderableDevices, quantities).forEach(device => {
        errors.push({
          property: 'unavailableItem',
          propertyName: `${device.device_manufacturer || ''} ${device.device_model || device.device_uuid}`.trim(),
          device_uuid: device.device_uuid,
          deviceType: device.device_type,
          missing: false,
          errors: [Strings.shipping.deviceUnavailableRemove],
        });
      });
    }
    setValidationErrors(errors);
  }, [targetOrgUuid, recipientName, address, quantities, returnKitId, orderableDevices, hasShipments, isReturnFlow, patientUuid, orderCategory]);

  const onRejectOrder = (reason) => {
    rejectOrder(orderId, reason).then(() => {
      getOrderDetails();
      setShowRejectModal(false);
      showNotification(Strings.shipping.orderRejectedSuccessfully);
      history.back();
    });
  }

  const getOrderDTO = React.useCallback((status) => {
    const items = buildCurrentOrderItems({
      hasShipments,
      isReturnFlow,
      originalItems: originalOrderSnapshot?.order_items,
      orderableDevices,
      quantities,
    });

    return {
      status,
      ...buildOrderObjectFromState({
        ...order,
        targetOrgUuid: hasShipments ? undefined : order.targetOrgUuid,
        billedOrgUuid: (hasShipments || isReturnFlow) ? undefined : order.billedOrgUuid,
        isPatientBilled: isReturnFlow ? undefined : order.isPatientBilled,
        orderItems: items,
      }),
    };
  }, [
    order,
    hasShipments,
    isReturnFlow,
    originalOrderSnapshot,
    orderableDevices,
    quantities,
  ]);

  const currentOrderSnapshot = React.useMemo(() => {
    const items = buildCurrentOrderItems({
      hasShipments,
      isReturnFlow,
      originalItems: originalOrderSnapshot?.order_items,
      orderableDevices,
      quantities,
    });

    return normalizeOrderForComparison({
      flow: isReturnFlow ? 'return' : 'outbound',
      ...buildOrderObjectFromState({
        ...order,
        orderItems: items,
      }),
    });
  }, [
    order,
    hasShipments,
    isReturnFlow,
    originalOrderSnapshot,
    orderableDevices,
    quantities,
  ]);

  const isOrderChanged = React.useMemo(() => {
    if (!originalOrderSnapshot || !orderLoaded || catalogState !== 'ready') {
      return false;
    }

    return !_.isEqual(currentOrderSnapshot, originalOrderSnapshot);
  }, [currentOrderSnapshot, originalOrderSnapshot, orderLoaded, catalogState]);

  const acceptButtonTitle = isOrderChanged
    ? 'Save Changes and Accept Order'
    : (Strings.shipping.acceptOrder || 'Accept Order');

  const onAcceptOrder = () => {
    const orderData = getOrderDTO();
    acceptOrder(orderId, orderData).then(resp => {
      if (resp?.type !== ACCEPT_ORDER_RESULT) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
        return;
      }
      showNotification(Strings.shipping.orderAcceptedSuccessfully);
      history.back();
    });
  }

  // Submit handler
  const onSubmit = (status) => {
    setIsSaving(true);

    const orderData = getOrderDTO(status);

    const saveAction = isEditMode
      ? updateOrderRequest(orderId, orderData)
      : createOrder(orderData);

    saveAction.then(resp => {
      if (resp?.type !== CREATE_ORDER_RESULT && resp?.type !== EDIT_ORDER_RESULT) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
        setIsSaving(false);
        return;
      }

      showNotification(Strings.formatString(Strings.itemSaved, Strings.shipping.order), 3000);
      history.back();
    });
  };

  const updateQuantity = React.useCallback((deviceUuid, variant, newQuantity) => {
    const device = orderableDevices.find(d => d.device_uuid === deviceUuid);
    const clamped = clampQuantity({ device, storedQuantities, variant, requested: newQuantity });
    updateOrder(prev => {
      if (variant) {
        return {
          ...prev,
          quantities: {
            ...prev.quantities,
            [deviceUuid]: {
              ...prev.quantities[deviceUuid],
              [variant]: clamped,
            },
          },
        };
      }
      return {
        ...prev,
        quantities: {
          ...prev.quantities,
          [deviceUuid]: clamped,
        },
      };
    });
  }, [updateOrder, orderableDevices, storedQuantities]);

  // While the catalog request has failed we cannot tell whether the stored
  // devices are still orderable, so they render as availability-unknown
  // (edits disabled) instead of the regular picker.
  const displayDevices = React.useMemo(() => (
    catalogState === 'error' ? buildUnknownAvailabilityDevices(storedOrderItems) : orderableDevices
  ), [catalogState, storedOrderItems, orderableDevices]);

  const deviceTypes = React.useMemo(() => {
    const types = {};
    displayDevices.forEach(d => {
      if (!types[d.device_type]) {
        types[d.device_type] = [];
      }
      types[d.device_type].push(d);
    }
    );
    return types;
  }, [displayDevices]);

  const getQuantitiesForDeviceType = deviceType => {
    const devices = deviceTypes[deviceType] || [];
    return devices.reduce((acc, d) => {
      acc[d.device_uuid] = sumQuantity(quantities[d.device_uuid]);
      return acc;
    }, {});
  };

  const statusHistoryInfo = statusHistory.map((entry, index) => (
    <div key={entry.id} className="form-group field field-object">
      <fieldset style={{display: 'flex'}}>
        <header className="header-with-button">
          {Strings.shipping.orderStatuses[entry.old_status] || entry.old_status} → {Strings.shipping.orderStatuses[entry.status] || entry.status}
          <div className='subheader'>
            {`${moment(entry.created_at).format(DATE_MONTH_DAY_YEAR)}, ${entry?.created_by_name || "System"}`}
          </div>
        </header>
        {entry.notes}
      </fieldset>
    </div>
  ));

  const shippingInfo = shipments?.length > 0 ? shipments?.map(s => (
    <div key={s.shipment_uuid} className="form-group field field-object" onClick={() => showShipmentDetails({ shipmentId: s.shipment_uuid, orderId, orgs })} style={{ cursor: 'pointer' }}>
      <fieldset style={{display: 'flex'}}>
        <header>{Strings.shipping.shipmentStatuses[s.status]}</header>
        {
          (s?.created_at && moment(s.created_at).isValid())
          ? `${Strings.createdAt}: ${moment(s.created_at).format(DATE_MONTH_DAY_YEAR_WITH_DASH)}`
          : '-'
        }
      </fieldset>
    </div>
  )) : null;

  const smartmeterOrderInfoContent = smartmeterOrderInfo ? (
    <>
      <div>{Strings.status}: {Strings.shipping.smartmeterOrderStatusEnum[smartmeterOrderInfo.status] || smartmeterOrderInfo.status}</div>
      <div>{Strings.shipping.vendorStatus}: {smartmeterOrderInfo.vendor_status}</div>
      <div>{Strings.shipping.shippingMethod}: {smartmeterOrderInfo.shipping_method}</div>
      <div>{Strings.submittedDate}: {smartmeterOrderInfo.submitted_at ? moment(smartmeterOrderInfo.submitted_at).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-'}</div>
      <div>{Strings.shipping.shippingDate}: {smartmeterOrderInfo.shipped_at ? moment(smartmeterOrderInfo.shipped_at).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-'}</div>
      <div>{Strings.shipping.deliveryDate}: {smartmeterOrderInfo.delivered_at ? moment(smartmeterOrderInfo.delivered_at).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-'}</div>
      {smartmeterOrderInfo.cancelled_at && (
        <div>{Strings.cancelledDate}: {smartmeterOrderInfo.cancelled_at ? moment(smartmeterOrderInfo.cancelled_at).format(DATE_MONTH_DAY_YEAR_WITH_DASH) : '-'}</div>
      )}
      {smartmeterOrderInfo.error_message && <div>{Strings.errorMessage}: {smartmeterOrderInfo.error_message}</div>}
      <div>{Strings.attempts}: {smartmeterOrderInfo.attempts}</div>
      {smartmeterOrderInfo.line_items?.length > 0 && (
        <table className="smartmeter-line-items" data-testid="smartmeter-line-items">
          <thead>
            <tr>
              <th>{Strings.shipping.smartmeterItemSku}</th>
              <th>{Strings.shipping.smartmeterItemName}</th>
              <th>{Strings.shipping.smartmeterItemQuantity}</th>
            </tr>
          </thead>
          <tbody>
            {smartmeterOrderInfo.line_items.map((item, i) => {
              const parts = smartmeterLineItemParts(item);
              return (
                <tr key={`${item.device_uuid}-${i}`}>
                  <td>{parts.sku}</td>
                  <td>
                    {parts.name}
                    {parts.overrideNote && <div className="smartmeter-sku-override">{parts.overrideNote}</div>}
                  </td>
                  <td>{parts.quantity}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  ) : null;

  const renderQuantityInput = (d, v) => {
    const currentQuantity = (v?.label ? quantities[d.device_uuid]?.[v.label] : quantities[d.device_uuid]) || 0;
    const otherVariantsQuantity = v?.label
      ? Object.entries(quantities[d.device_uuid] || {}).reduce(
        (total, [variantLabel, qty]) => (variantLabel !== v.label ? total + qty : total),
        0,
      )
      : 0;
    const unavailableLine = Boolean(d.unavailable || (v?.label && d.unavailableVariants?.includes(v.label)));
    const baseDisabled = hasShipments || !(canEdit || canEditOrdersInReview) || Boolean(d.availabilityUnknown);
    // The single-variant lock only stops a second variant from being added:
    // − ignores it so a line holding a quantity can always be removed, and
    // unavailable (decrease-only) lines are exempt from it.
    const variantLock = Boolean(!unavailableLine && v?.label && otherVariantsQuantity > 0);
    const isSmartmeter = d.device_manufacturer === 'smartmeter';
    const errorsForTooltip = validationErrors
      .filter(e => e.device_uuid === d.device_uuid && (v?.label ? e.variant_label === v.label : !e.variant_label))
      .flatMap(e => e.errors || []);

    return (
      <div className="quantity-input-container">
        {errorsForTooltip &&
          errorsForTooltip.map((item, i) => (
            <span
              key={`error-tooltip-${i}`}
              className="input-error quantity"
              data-tooltip-content={item}
              data-tooltip-id="tooltip"
            />
          ))}
        <Button
          testid={`decrease-${d.device_uuid}${v ? `-${v.label}` : ''}`}
          class={HEADER_BUTTON_DARK_BLUE}
          onClick={() => updateQuantity(d.device_uuid, v?.label, currentQuantity - 1)}
          disabled={currentQuantity === 0 || baseDisabled}
          title="-"
        />
        <input
          id={`quantity-${d.device_uuid}${v ? `-${v.label}` : ''}`}
          data-testid={`quantity-${d.device_uuid}${v ? `-${v.label}` : ''}`}
          type="number"
          min={0}
          max={isSmartmeter ? MAX_SMARTMETER_QUANTITY : MAX_QUANTITY}
          value={currentQuantity}
          onFocus={e => e.target.select()}
          onChange={e => {
            const newQuantity = parseInt(e.target.value, 10);
            updateQuantity(d.device_uuid, v?.label, isNaN(newQuantity) ? 0 : newQuantity);
          }}
          disabled={baseDisabled || variantLock}
        />
        <Button
          testid={`increase-${d.device_uuid}${v ? `-${v.label}` : ''}`}
          class={HEADER_BUTTON_DARK_BLUE}
          onClick={() => updateQuantity(d.device_uuid, v?.label, currentQuantity + 1)}
          disabled={baseDisabled || variantLock || unavailableLine || currentQuantity >= (isSmartmeter ? MAX_SMARTMETER_QUANTITY : MAX_QUANTITY)}
          title="+"
        />
      </div>
    );
  };
  const deviceGridView = (
    <div className="config-modules">
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        className="scrollbar-right-margin-min config-modules-nav"
      >
        <div className="config-modules-label">{Strings.modules}</div>
        <div className="tab-buttons-row config-modules-tabs">
          {Object.keys(deviceTypes).map(deviceType => (
            <button
              key={deviceType}
              type="button"
              className={`tabButton tab-label${activeDeviceType === deviceType ? ' active-tab-label' : ''}`}
              onClick={() => setActiveDeviceType(deviceType)}
            >
              {deviceType}
              {Object.values(getQuantitiesForDeviceType(deviceType)).some(q => q > 0) && <span className="quantity-chip">{Object.values(getQuantitiesForDeviceType(deviceType)).reduce((acc, q) => acc + q, 0)}</span>}
              {validationErrors.some(error => error.deviceType === deviceType) && <span className="input-error module-error-icon left" />}
            </button>
          ))}
        </div>
      </OverlayScrollbarsComponent>
      <div
        className="config-modules-content"
      >
        {activeDeviceType && deviceTypes[activeDeviceType] ? (
          <div className="rjsf">
            <fieldset>
              <div className="form-group field field-object leaf" key={activeDeviceType}>
                <fieldset>
                  <header>{activeDeviceType}</header>
                  {deviceTypes[activeDeviceType].map(d => {
                    const q = quantities[d.device_uuid];
                    const currentVariantLabels = d.smartmeter_variant_required
                      ? (d.smartmeter_variants || []).map(cv => cv.label)
                      : [];
                    // Persisted variant lines whose label is no longer offered
                    // by the catalog device (or whose device has no variants
                    // anymore) — rendered below so they stay removable.
                    const storedOnlyLabels = q && typeof q === 'object'
                      ? Object.keys(q).filter(label => label !== VARIANT_NOT_YET_SELECTED && !currentVariantLabels.includes(label))
                      : [];
                    const hasUnselectedVariantQuantity = Boolean(
                      d.smartmeter_variant_required
                      && d.smartmeter_variants?.length > 0
                      && q?.[VARIANT_NOT_YET_SELECTED] > 0,
                    );
                    const hasErrors = validationErrors.some(e => e.device_uuid === d.device_uuid);
                    return (
                      <div
                        className={`form-group field field-object ${(d.smartmeter_variants?.length > 0 || storedOnlyLabels.length > 0) ? '' : 'leaf'} ${q && !d.smartmeter_variant_required ? 'has-quantity' : ''} ${hasErrors ? ' has-errors' : ''}`}
                        key={d.device_uuid}
                      >
                        <header className="subheader">
                          {d.device_manufacturer} {d.device_model}
                          {d.unavailable && (
                            <div
                              className="signal-alert info"
                              data-tooltip-content={Strings.shipping.deviceUnavailable}
                              data-tooltip-id="tooltip"
                            />
                          )}
                        </header>
                        {hasUnselectedVariantQuantity && (
                          <div className="form-group field field-object has-quantity">
                            <SelectField
                              name={`variant-select-${d.device_uuid}`}
                              id={`variant-select-${d.device_uuid}`}
                              label={Strings.formatString(Strings.shipping.selectVariantForDevice, q?.[VARIANT_NOT_YET_SELECTED] || 0)}
                              value=""
                              placeholder={Strings.shipping.selectVariant}
                              data={d.smartmeter_variants.map(v => ({ value: v.label, label: v.label }))}
                              onChange={o => {
                                const selectedVariant = o.value;
                                const currentQuantity = q?.[VARIANT_NOT_YET_SELECTED] || 0;
                                updateOrder(prev => {
                                  const currentDeviceQuantities = prev.quantities[d.device_uuid];
                                  if (!currentDeviceQuantities || typeof currentDeviceQuantities !== 'object') {
                                    return prev;
                                  }
                                  const newDeviceQuantities = { ...currentDeviceQuantities };
                                  if (selectedVariant !== VARIANT_NOT_YET_SELECTED) {
                                    newDeviceQuantities[selectedVariant] = (newDeviceQuantities[selectedVariant] || 0) + currentQuantity;
                                    delete newDeviceQuantities[VARIANT_NOT_YET_SELECTED];
                                  }
                                  return {
                                    ...prev,
                                    quantities: {
                                      ...prev.quantities,
                                      [d.device_uuid]: newDeviceQuantities,
                                    },
                                  };
                                });
                              }}
                              isRequired
                              disabled={hasShipments || !(canEdit || canEditOrdersInReview) || Boolean(d.availabilityUnknown)}
                              highlightInvalid={highlightInvalidFields}
                              errorsForTooltip={validationErrors.find(v => v.property === 'variant' && v.device_uuid === d.device_uuid)?.errors || []}
                            />
                          </div>
                        )}
                        {(d.smartmeter_variant_required && d.smartmeter_variants?.length > 0 && !hasUnselectedVariantQuantity)
                        && d.smartmeter_variants.map(v => (
                          <div className={`form-group field field-object ${q?.[v.label] ? 'has-quantity' : ''}`} key={`${d.device_uuid}-${v.label}`}>
                            <header className="subheader">{v.label}</header>
                            {renderQuantityInput(d, v)}
                          </div>
                        ))}
                        {storedOnlyLabels.map(label => (
                          <div className={`form-group field field-object ${q?.[label] ? 'has-quantity' : ''}`} key={`${d.device_uuid}-${label}`}>
                            <header className="subheader" style={{ position: 'relative' }}>
                              {label}
                              {!d.unavailable && (
                                <div
                                  className="input-error quantity"
                                  data-tooltip-content={Strings.shipping.deviceUnavailable}
                                  data-tooltip-id="tooltip"
                                />
                              )}
                            </header>
                            {renderQuantityInput(d, { label })}
                          </div>
                        ))}
                        {!d.smartmeter_variant_required && !d.availabilityUnknown && !storedOnlyLabels.length
                          && (!d.unavailable || typeof q !== 'object')
                          && renderQuantityInput(d, q && typeof q === 'object' ? { label: VARIANT_NOT_YET_SELECTED } : undefined)}
                      </div>
                    );
                  })}
                </fieldset>
              </div>
            </fieldset>
          </div>
        ) : null}
      </div>
    </div>
  );

  const appliedFilters = React.useMemo(() => {
    return filters.filter(f => f.group === 'DeviceType' && f.state);
  }, [filters]);

  const devicesListView = (
    <>
      <AdvancedFiltersBar />
      <Table3
        data={
          React.useMemo(() => flattenDevicesToRows(
            displayDevices.filter(d => appliedFilters.some(f => f.value === d.device_type) || appliedFilters.length === 0),
            quantities,
          ), [displayDevices, quantities, appliedFilters])
        }
        columns={React.useMemo(() => [
          columnHelper.accessor('device_type', {
            header: Strings.deviceType,
            accessorFn: row => row.device.device_type,
          }),
          columnHelper.accessor('device_manufacturer', {
            header: Strings.manufacturer,
            accessorFn: row => row.device.device_manufacturer,
          }),
          columnHelper.accessor('device_model', {
            header: Strings.deviceModel,
            cell: renderDeviceModelCell,
          }),
          columnHelper.accessor('quantity', {
            header: Strings.quantity,
            cell: ({ row }) => renderQuantityInput(row.original.device, row.original.variant),
          }),
        ], [quantities, hasShipments])}
      />
    </>
  );

  const catalogErrorPanel = catalogState === 'error' ? (
    <div className="form-group field field-object leaf">
      <fieldset>
        <header className="header-with-button">
          {Strings.shipping.catalogLoadFailed}
          <div className="buttons right">
            <Button
              class={HEADER_BUTTON_DARK_BLUE}
              title={Strings.shipping.catalogRetry}
              onClick={() => setCatalogRetryCounter(c => c + 1)}
            />
          </div>
        </header>
      </fieldset>
    </div>
  ) : null;

  const devicesContent = (
    <>
      {catalogErrorPanel}
      {Object.keys(deviceTypes).length === 0 ? <p>{Strings.noDevicesAvailable}</p> : deviceView === 'grid' ? deviceGridView : devicesListView}
    </>
  );
  
  const orderDetailsContent = (
    <>
      {organizations.length > 0 && (!patientUuid || isReturnFlow) && (
        <SelectField
          name="targetOrgUuid"
          id="targetOrgUuid"
          label={Strings.shipping.targetOrganization}
          value={targetOrgUuid}
          data={suggestedReturnTargets?.length > 0 ? suggestedReturnTargets.map(o => ({ value: o.org_uuid, label: o.name })) : organizations.map(o => ({ value: o.masked_id, label: o.name }))}
          onChange={o => {
            userSelectedOrg.current = true;
            updateOrder({ targetOrgUuid: o.value });
          }}
          disabled={!(canEdit) || orderStatus === OrderStatusEnum.pending_review}
          isRequired
          isSearchable
          highlightInvalid={highlightInvalidFields}
          errorsForTooltip={validationErrors.find(v => v.property === 'targetOrgUuid')?.errors || []}
        />
      )}
      {organizations.length > 0 && !patientUuid && !isReturnFlow && (
        <SelectField
          name="billedOrgUuid"
          id="billedOrgUuid"
          label={Strings.shipping.billedOrganization}
          value={billedOrgUuid}
          data={organizations.map(o => ({ value: o.masked_id, label: o.name }))}
          onChange={o => updateOrder({ billedOrgUuid: o.value })}
          disabled={!(canEdit) || orderStatus === OrderStatusEnum.pending_review}
          isSearchable
        />
      )}
      {!patientUuid && !isReturnFlow && (
        <SelectField
          name="order_type"
          id="order_type"
          label={Strings.shipping.orderType}
          value={orderType}
          data={Object.values(OrderTypeEnum).map(type => ({ value: type, label: Strings.shipping.orderTypes[type] }))}
          onChange={o => updateOrder({ orderType: o.value })}
          disabled={!(canEdit) || orderStatus === OrderStatusEnum.pending_review}
        />
      )}
      {patientUuid && !isReturnFlow && (
        <Input
          name="isPatientBilled"
          id="isPatientBilled"
          label={Strings.shipping.isPatientBilled}
          type="checkbox"
          checked={isPatientBilled}
          onChange={e => updateOrder({ isPatientBilled: e.target.checked })}
          disabled={!(canEdit) || orderStatus === OrderStatusEnum.pending_review}
          rowClassName="checkbox-row"
        />
      )}
      {!isReturnFlow && (
        <Input
          name="providerOrderNumber"
          id="providerOrderNumber"
          label={Strings.shipping.providerOrderNumber}
          type="text"
          value={providerOrderNumber}
          onChange={e => updateOrder({ providerOrderNumber: e.target.value })}
          disabled={!(canEdit) || orderStatus === OrderStatusEnum.pending_review}
        />
      )}
      <TextArea
        name="notes"
        id="notes"
        label={Strings.capPatient.notes}
        value={notes}
        onChange={e => updateOrder({ notes: e.target.value })}
        disabled={!(canEdit) && !(canEditOrdersInReview)}
      />
    </>
  );

  const shippingDetailsContent = (
    <>
      <div className="form-group field field-object leaf">
        <fieldset>
          <SelectField
            name="shippingMethod"
            id="shippingMethod"
            label={Strings.shipping.shippingMethod}
            value={shippingMethod}
            data={ShippingMethods.map(m => ({ label: m, value: m }))}
            onChange={o => updateOrder({ shippingMethod: o.value })}
            disabled={!(canEdit || canEditOrdersInReview)}
          />
        </fieldset>
      </div>
      <div className="form-group field field-object leaf">
        <fieldset>
          <header>
            {Strings.shipping.contactDetails}
            {proposedRecipient && (
              <div className='subheader'>
                {`${patientUuid ? Strings.shipping.patientContactDetails : Strings.shipping.organizationContactDetails}: ${Strings.name}: ${proposedRecipient?.name || '-'}, ${Strings.phone}: ${proposedRecipient?.phone || '-'}, ${Strings.email}: ${proposedRecipient?.email || '-'}`}
              </div>
            )}
          </header>

          <Input
            name="recipientName"
            id="recipientName"
            label={Strings.shipping.recipientName}
            type="text"
            value={recipientName}
            onChange={e => updateOrder({ recipientName: e.target.value })}
            disabled={!(canEdit || canEditOrdersInReview)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'recipientName')?.errors || []}
          />
          <Input
            name="email"
            id="email"
            label={Strings.shipping.recipientEmail}
            type="email"
            value={email}
            onChange={e => updateOrder({ email: e.target.value })}
            disabled={!(canEdit || canEditOrdersInReview)}
          />
          <PhoneInputField
            name="phone"
            id="phone"
            label={Strings.shipping.recipientPhone}
            type="text"
            value={phone}
            onChange={value => updateOrder({ phone: value })}
            disabled={!(canEdit || canEditOrdersInReview)}
            rowClassName="phone-input-abs"
          />
        </fieldset>
      </div>
      <div className="form-group field field-object leaf">
        <fieldset>
          <header>
            {Strings.shipping.shippingAddress}
            {proposedAddress && (
              <div className='subheader'>
                {`${patientUuid ? Strings.shipping.patientAddress : Strings.shipping.organizationAddress}: ${proposedAddress || '-'}`}
              </div>
            )}
          </header>
          <AddressFields
            address={address}
            onAddressChange={onAddressChange}
            isRequired
            highlightInvalid={highlightInvalidFields}
            disabled={!(canEdit || canEditOrdersInReview)}
          />
        </fieldset>
      </div>
    </>
  );

  const showDraftButton = orderId === 'new' || orderStatus === OrderStatusEnum.draft;
  const showAcceptButton = orderStatus === OrderStatusEnum.pending_review && canEditOrdersInReview;
  const showRejectButton = orderStatus === OrderStatusEnum.pending_review && canEditOrdersInReview;
  const showSaveButton = orderId === 'new' || orderStatus === OrderStatusEnum.draft || orderStatus === OrderStatusEnum.open;
  const showSendToReviewButton = orderStatus === OrderStatusEnum.rejected_by_doctor && (canEdit || canEditOrdersInReview);

  return (
    <>
      <PageHeader
        onBackClick={() => history.back()}
        left={
          orderId === 'new' && isReturnFlow
          ? Strings.shipping.newReturnOrder
          : orderId === 'new'
          ? Strings.shipping.newOrder
          : orderId && orderStatus
          ? `${isReturnFlow ? Strings.shipping.returnOrder : Strings.shipping.order} ${orderId} - ${Strings.shipping.orderStatuses[orderStatus] || orderStatus}`
          : Strings.shipping.orderDetails
        }
        right={(
          <>
            {showDraftButton && canEdit && (
              <div
                className="button-container"
                data-tooltip-html={objectToTooltip(getTooltipArrayFromValidationErrors(validationErrors))}
                data-tooltip-id={validationErrors.length > 0 ? "tooltip" : undefined}
                onMouseEnter={() => setHighlightInvalidFields(true)}
                onMouseLeave={() => setHighlightInvalidFields(false)}
              >
                <Button
                  onClick={() => onSubmit(OrderStatusEnum.draft)}
                  class={HEADER_BUTTON_DARK_BLUE}
                  title={Strings.saveAsDraft || 'Save as Draft'}
                  disabled={isSaving || validationErrors.length > 0 || catalogNotReady}
                />
              </div>
            )}
            {showRejectButton && (
              <div
                className="button-container"
                data-tooltip-html={objectToTooltip(getTooltipArrayFromValidationErrors(validationErrors))}
                data-tooltip-id={validationErrors.length > 0 ? "tooltip" : undefined}
                onMouseEnter={() => setHighlightInvalidFields(true)}
                onMouseLeave={() => setHighlightInvalidFields(false)}
              >
                <Button
                  onClick={() => setShowRejectModal(true)}
                  class="remove"
                  title={Strings.shipping.rejectOrder || 'Reject Order'}
                  disabled={isSaving}
                />
              </div>
            )}
            {showAcceptButton && (
              <div
                className="button-container"
                data-tooltip-html={objectToTooltip(getTooltipArrayFromValidationErrors(validationErrors))}
                data-tooltip-id={validationErrors.length > 0 ? "tooltip" : undefined}
                onMouseEnter={() => setHighlightInvalidFields(true)}
                onMouseLeave={() => setHighlightInvalidFields(false)}
              >
                <Button
                  onClick={onAcceptOrder}
                  class={HEADER_BUTTON_DARK_BLUE}
                  title={acceptButtonTitle}
                  disabled={isSaving || validationErrors.length > 0 || catalogNotReady}
                />
              </div>
            )}
            {showSendToReviewButton && (
              <div
                className="button-container"
                data-tooltip-html={objectToTooltip(getTooltipArrayFromValidationErrors(validationErrors))}
                data-tooltip-id={validationErrors.length > 0 ? "tooltip" : undefined}
                onMouseEnter={() => setHighlightInvalidFields(true)}
                onMouseLeave={() => setHighlightInvalidFields(false)}
              >
                <Button
                  onClick={() => onSubmit(OrderStatusEnum.pending_review)}
                  class={HEADER_BUTTON_DARK_BLUE}
                  title={Strings.shipping.sendToReview || 'Send to Review'}
                  disabled={isSaving || validationErrors.length > 0 || catalogNotReady}
                />
              </div>
            )}
            {showSaveButton && canEdit && (
              <div
                className="button-container"
                data-tooltip-html={objectToTooltip(getTooltipArrayFromValidationErrors(validationErrors))}
                data-tooltip-id={validationErrors.length > 0 ? "tooltip" : undefined}
                onMouseEnter={() => setHighlightInvalidFields(true)}
                onMouseLeave={() => setHighlightInvalidFields(false)}
              >
                <Button
                  onClick={() => onSubmit((orderId === 'new' || orderStatus === OrderStatusEnum.draft) ? OrderStatusEnum.open : orderStatus)}
                  class={HEADER_BUTTON_DARK_BLUE}
                  title={(orderId === 'new' || orderStatus === OrderStatusEnum.draft)
                    ? (Strings.shipping.placeOrder)
                    : (Strings.saveChanges)}
                  disabled={isSaving || validationErrors.length > 0 || catalogNotReady}
                />
              </div>
            )}
          </>
        )}
      />
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        className="scrollbar-right-margin config-tab-container"
      >
        <EndedEnrollmentShippingWarning order={{
          flow: isReturnFlow ? 'return' : 'outbound',
          status: orderStatus,
          patient_uuid: order.patientUuid,
          ended_enrollments: order.endedEnrollments,
          shipments,
        }} />
        <div className="rjsf">
          <fieldset>
            {shipments?.length > 0 && (
              <div className="form-group field field-object leaf">
                <fieldset>
                  <header>{Strings.shipping.shipments}</header>
                  {shippingInfo}
                </fieldset>
              </div>
            )}
            {smartmeterOrderInfo && (
              <div className="form-group field field-object leaf">
                <fieldset>
                  <header>{`${Strings.shipping.smartmeterOrder} ${smartmeterOrderInfo.vendor_order_number}`}</header>
                  {smartmeterOrderInfoContent}
                </fieldset>
              </div>
            )}
            {statusHistory?.length > 0 && (
              <div className="form-group field field-object">
                <fieldset>
                  <header>{Strings.history}</header>
                  {statusHistoryInfo}
                </fieldset>
              </div>
            )}
            {isReturnFlow ? (
              <div className="form-group field field-object">
                <fieldset className={highlightInvalidFields && hasOrderItemsValidationError ? 'invalid-control-background' : ''}>
                  <header>{Strings.kitId}</header>
                  {returnKitId}
                </fieldset>
              </div>
            ) : (
              <div className="form-group field field-object">
                <fieldset className={highlightInvalidFields && hasOrderItemsValidationError ? 'invalid-control-background' : ''}>
                  <header className="header-with-button page-header required-asterisk">
                    {Strings.devices || 'Devices'}
                    <div className="buttons right">
                      {deviceView === 'list' && (
                        <AdvancedFilters>
                          <Filter definition={getFiltersDefinition({ includeHub: true }).DeviceType} />
                        </AdvancedFilters>
                      )}
                      {deviceView === 'grid' && (
                        <Button
                          class="show-as-list-button"
                          onClick={() => setDeviceView('list')}
                        />
                      )}
                      {deviceView === 'list' && (
                        <Button
                          class="show-as-grid-button"
                          onClick={() => setDeviceView('grid')}
                        />
                      )}
                    </div>
                  </header>
                  {data?.order?.vitals?.length > 0 && (
                    <div className='subheader'>
                      {Strings.vitals}: {data?.order?.vitals.join(', ')}
                    </div>
                  )}
                  {data?.order?.deviceType && (
                    <div className='subheader'>
                      {Strings.deviceType}: {data?.order?.deviceType}
                    </div>
                  )}
                  {patientNotes?.length > 0 && (
                    <div className='subheader'>
                      {Strings.capPatient.notes}: {patientNotes.map(n => n.content).join('; ')}
                    </div>
                  )}
                  {devicesContent}
                </fieldset>
              </div>
            )}
            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.orderDetails || 'Order Details'}</header>
                {orderDetailsContent}
              </fieldset>
            </div>
            <div className="form-group field field-object">
              <fieldset>
                <header>{Strings.shippingDetails || 'Shipping Details'}</header>
                {shippingDetailsContent}
              </fieldset>
            </div>
          </fieldset>
        </div>
      </OverlayScrollbarsComponent>
      {showRejectModal && (
        <RejectOrderModal
          onCancel={() => {setShowRejectModal(false);}}
          onConfirm={(reason) => onRejectOrder(reason)}
        />
      )}
    </>
  );
}

OrderDetails.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      order: PropTypes.object,
    }),
  }),
  onCancel: PropTypes.func,
  createOrder: PropTypes.func,
  updateOrder: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  getOrder: id => dispatch(shippingActions.getOrder(id)),
  createOrder: (orderData) => dispatch(shippingActions.createOrder(orderData)),
  updateOrder: (id, orderData) => dispatch(shippingActions.editOrder(id, orderData)),
  rejectOrder: (order, reason) => dispatch(shippingActions.rejectOrder(order, {reason})),
  acceptOrder: (id, orderData) => dispatch(shippingActions.acceptOrder(id, orderData)),
  getCatalog: params => dispatch(shippingActions.getCatalog(params)),
  getOrganizations: () => dispatch(orgActions.queryOrganizations({ limit: 1000, offset: 0 })),
  getOrgConfig: orgId => dispatch(orgActions.getOrgConfig({organizationId: orgId})),
  showShipmentDetails: data => dispatch(openModalAction('shipment-details', data)),
  getOrgs: request => dispatch(orgActions.getOrgs(request)),
  getNotes: patientId => dispatch(patientActions.getNotes(patientId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetails);
